import type React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { ConversationPlan, PlanStatus } from '@knownow/domain'
import { useProfile } from '@/hooks/use-profile'
import { usePlans } from '@/hooks/use-plans'
import { deletePlan } from '@/lib/plan-api-client'
import { TitleSection } from '@/components/main-page/title-section'
import { StatsRow, type StatItem } from '@/components/main-page/stats-row'
import {
  FilterToolbar,
  type FilterKey,
  type FilterOption,
} from '@/components/main-page/filter-toolbar'
import { PlanCard } from '@/components/main-page/plan-card'
import { StartNewCard } from '@/components/main-page/start-new-card'
import { DeletePlanDialog } from '@/components/main-page/delete-plan-dialog'
import { isPlanConfigured } from '@/components/main-page/is-plan-configured'

// 'reviewing' is intentionally not surfaced as its own pill — those plans
// remain reachable via the "All" filter until the design calls for it.
type ToolbarFilterKey = Exclude<FilterKey, 'reviewing'>
const FILTER_KEYS: readonly ToolbarFilterKey[] = ['all', 'active', 'draft', 'completed', 'archived']
const FILTER_LABELS: Record<ToolbarFilterKey, string> = {
  all: 'All',
  draft: 'Drafts',
  active: 'Active',
  completed: 'Completed',
  archived: 'Archived',
}

function FolderIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )
}
function ChatIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}
function TrendIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  )
}
function PeopleIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function matchesFilter(status: PlanStatus, key: FilterKey): boolean {
  return key === 'all' || status === key
}

function matchesQuery(title: string, description: string, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  return title.toLowerCase().includes(q) || description.toLowerCase().includes(q)
}

export function PlansListPage(): React.ReactElement {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { plans, loading, removePlan } = usePlans(orgSlug)
  const { organizations } = useProfile()
  const orgName = organizations.find((o) => o.slug === orgSlug)?.name ?? null

  const [filter, setFilter] = useState<FilterKey>('all')
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const [planAwaitingConfirm, setPlanAwaitingConfirm] = useState<ConversationPlan | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const newPlanHref = `/${orgSlug ?? ''}/plans/new`

  const performDelete = useCallback(
    async (plan: ConversationPlan): Promise<void> => {
      if (!orgSlug) return
      setDeleting(true)
      setDeleteError(null)
      try {
        await deletePlan(orgSlug, plan.id)
        // Realtime won't deliver the soft-delete UPDATE (the row becomes
        // unreadable to this client once `deleted_at` is set), so drop it
        // from local state explicitly.
        removePlan(plan.id)
        setPlanAwaitingConfirm(null)
      } catch (err) {
        console.error('Failed to delete plan', err)
        setDeleteError("Couldn't delete the plan. Please try again.")
      } finally {
        setDeleting(false)
      }
    },
    [orgSlug, removePlan],
  )

  const handleDeleteRequest = useCallback(
    (plan: ConversationPlan): void => {
      setDeleteError(null)
      if (isPlanConfigured(plan)) {
        setPlanAwaitingConfirm(plan)
      } else {
        void performDelete(plan)
      }
    },
    [performDelete],
  )

  const dismissConfirm = useCallback((): void => {
    setPlanAwaitingConfirm(null)
    setDeleteError(null)
  }, [])

  const counts = useMemo(() => {
    const init: Record<FilterKey, number> = {
      all: plans.length,
      draft: 0,
      reviewing: 0,
      active: 0,
      completed: 0,
      archived: 0,
    }
    for (const p of plans) init[p.status] += 1
    return init
  }, [plans])

  const filterOptions: readonly FilterOption[] = FILTER_KEYS.map((k) => ({
    key: k,
    label: FILTER_LABELS[k],
    count: counts[k],
  }))

  const visiblePlans = useMemo(
    () =>
      plans.filter(
        (p) =>
          matchesFilter(p.status, filter) &&
          matchesQuery(p.title, p.description, search),
      ),
    [plans, filter, search],
  )

  const stats: readonly StatItem[] = [
    {
      label: 'Active plans',
      value: counts.active.toString(),
      hint: `of ${plans.length} total`,
      icon: <FolderIcon />,
    },
    {
      label: 'Total responses',
      value: '—',
      hint: 'this month',
      icon: <ChatIcon />,
    },
    {
      label: 'Avg. completion',
      value: '—',
      hint: 'across plans',
      icon: <TrendIcon />,
    },
    {
      label: 'Active facilitators',
      value: '—',
      hint: 'across cohorts',
      icon: <PeopleIcon />,
    },
  ]

  return (
    <div className="mx-auto max-w-6xl px-6 pb-16" data-testid="plans-list">
      <TitleSection orgName={orgName} newPlanHref={newPlanHref} />

      <StatsRow stats={stats} />

      <FilterToolbar
        options={filterOptions}
        activeKey={filter}
        onSelect={setFilter}
        searchQuery={search}
        onSearchChange={setSearch}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <div className="flex items-baseline justify-between pb-4">
        <h2 className="font-serif text-2xl text-foreground">All plans</h2>
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          {visiblePlans.length} {visiblePlans.length === 1 ? 'item' : 'items'} · sorted by recent
        </span>
      </div>

      {loading && (
        <div data-testid="plans-loading" className="text-sm text-muted-foreground">
          Loading plans...
        </div>
      )}

      {!loading && (
        <div
          className={
            viewMode === 'card'
              ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
              : 'flex flex-col gap-3'
          }
        >
          <StartNewCard newPlanHref={newPlanHref} />
          {visiblePlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              orgSlug={orgSlug ?? ''}
              ownerName={null}
              responses={null}
              onDelete={handleDeleteRequest}
            />
          ))}
        </div>
      )}

      {!loading && plans.length === 0 && (
        <p
          data-testid="plans-empty"
          className="mt-6 text-sm text-muted-foreground"
        >
          No plans yet. Create your first conversation plan to get started.
        </p>
      )}

      {!loading && plans.length > 0 && visiblePlans.length === 0 && (
        <p
          data-testid="plans-no-match"
          className="mt-6 text-sm text-muted-foreground"
        >
          No plans match your filter or search.
        </p>
      )}

      {planAwaitingConfirm && (
        <DeletePlanDialog
          planTitle={planAwaitingConfirm.title}
          busy={deleting}
          error={deleteError}
          onCancel={dismissConfirm}
          onConfirm={() => void performDelete(planAwaitingConfirm)}
        />
      )}
    </div>
  )
}
